import { Component } from '@theme/component';
/* [Modified by Codex at 2026-10-27] 让灯箱滚动锁定随组件清理 */
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  isLowPowerDevice,
  lockScroll,
  unlockScroll,
} from '@theme/utilities';
/* [End] */
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/* [Modified by Codex at 2026-10-27] 支持整图与铺宽切换并仅由关闭按钮退出 */
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLButtonElement[]} [zoomButtons] - Image size toggle buttons.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #pendingImages = /** @type {Map<HTMLImageElement, HTMLImageElement>} */ (new Map());
  #controller = new AbortController();
  #desktopQuery = window.matchMedia('(min-width: 750px)');
  #activeIndex = 0;
  #closing = false;
  /** @type {HTMLElement | null} */
  #returnFocus = null;

  connectedCallback() {
    super.connectedCallback();
    this.#connectListeners();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#connectListeners();
    if (this.refs.dialog.open) this.selectThumbnail(this.#activeIndex, { behavior: 'instant' });
  }

  #connectListeners() {
    this.#controller.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.refs.dialog.addEventListener('scroll', this.handleScroll, { signal });
    this.refs.dialog.addEventListener('cancel', this.#preventCancel, { signal });
    this.#desktopQuery.addEventListener('change', this.#handleBreakpointChange, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.handleScroll.cancel();
    for (const pending of this.#pendingImages.values()) {
      pending.onload = null;
      pending.onerror = null;
      pending.removeAttribute('src');
    }
    this.#pendingImages.clear();
    unlockScroll(this.refs.dialog);
  }

  /** @param {Event} event - Native dialog cancellation request. */
  #preventCancel = (event) => event.preventDefault();

  #handleBreakpointChange = () => {
    if (this.refs.dialog.open) this.selectThumbnail(this.#activeIndex, { behavior: 'instant' });
  };

  /** @param {'fit' | 'width'} mode - Desktop image display mode. */
  #setImageMode(mode) {
    this.refs.dialog.dataset.zoomMode = mode;
    for (const button of this.refs.zoomButtons ?? []) {
      const active = button.closest('[data-active="true"]');
      button.tabIndex = active ? 0 : -1;
      if (this.#desktopQuery.matches) {
        button.setAttribute('aria-pressed', `${mode === 'width' && Boolean(active)}`);
      } else {
        button.removeAttribute('aria-pressed');
      }
    }
  }

  /**
   * Toggles desktop fit/width sizing or delegates mobile zoom to the touch wrapper.
   * @param {number} index - Index of the image to resize.
   * @param {MouseEvent} event - Click or native keyboard button activation.
   */
  toggleImageSize(index, event) {
    if (!this.refs.dialog.open || this.#closing) return;
    const target = this.refs.media[index];
    if (!target?.classList.contains('product-media-container--image') || index !== this.#activeIndex) return;
    event.preventDefault();
    if (!this.#desktopQuery.matches) {
      const wrapper = /** @type {import('./drag-zoom-wrapper').DragZoomWrapper | null} */ (
        target.querySelector('drag-zoom-wrapper')
      );
      wrapper?.toggleZoom?.();
      return;
    }
    this.#setImageMode(this.refs.dialog.dataset.zoomMode === 'width' ? 'fit' : 'width');
    this.refs.dialog.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    this.loadHighResolutionImage(target);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media } = this.refs;
    const targetImage = media[index];
    if (!targetImage || dialog.open) return;
    this.#closing = false;
    this.#returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const open = () => {
      dialog.showModal();
      lockScroll(dialog);
      this.selectThumbnail(index, { behavior: 'instant' });
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (this.#desktopQuery.matches || !supportsViewTransitions() || isLowPowerDevice() || !sourceImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    try {
      await startViewTransition(() => {
        open();
        sourceImage.style.removeProperty('view-transition-name');
        targetImage.style.setProperty('view-transition-name', itemTransitionName);
      });
    } catch {
      if (!dialog.open && this.isConnected) open();
    } finally {
      sourceImage.style.removeProperty('view-transition-name');
      document.documentElement.style.removeProperty('--gallery-media-focal-point');
      targetImage.style.removeProperty('view-transition-name');
    }

  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#pendingImages.has(image)) return false;

    const updateImage = () => {
      if (!this.isConnected || !image.isConnected) return;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = highResolutionUrl;
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    if (this.#highResImagesLoaded.has(highResolutionUrl)) {
      updateImage();
      return;
    }

    const newImage = new Image();
    this.#pendingImages.set(image, newImage);

    // Preserve the original node, intrinsic dimensions, focal point and component refs.
    newImage.onload = () => {
      this.#pendingImages.delete(image);
      updateImage();
    };
    newImage.onerror = () => this.#pendingImages.delete(image);

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    if (!this.refs.dialog.open || this.#desktopQuery.matches) return;
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    if (!this.isConnected || !this.refs.dialog.open || this.#desktopQuery.matches) return;
    const activeIndex = media.indexOf(mostVisibleElement);
    this.#activeIndex = activeIndex;
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;
    if (!dialog.open || this.#closing) return;

    if (this.#desktopQuery.matches || !supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    const activeIndex = this.#activeIndex;
    const mostVisibleElement = media[activeIndex];
    if (!mostVisibleElement) return this.closeDialog();
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    this.#closing = true;
    dialog.classList.add('dialog--closed');

    try {
      await onAnimationEnd(this.refs.thumbnails);
      if (!this.isConnected || !dialog.open) return;

      mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

      await startViewTransition(() => {
        mostVisibleElement.style.removeProperty('view-transition-name');
        slide.style.setProperty('view-transition-name', itemTransitionName);
        this.closeDialog();
      });
    } catch {
      this.closeDialog();
    } finally {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.removeProperty('view-transition-name');
      dialog.classList.remove('dialog--closed');
      document.documentElement.style.removeProperty('--gallery-media-focal-point');
      this.#closing = false;
    }
  }

  closeDialog() {
    const { dialog } = this.refs;
    this.handleScroll.cancel();
    dialog.close();
    unlockScroll(dialog);
    this.#setImageMode('fit');
    this.#closing = false;
    window.dispatchEvent(new DialogCloseEvent());
    if (this.#returnFocus?.isConnected) this.#returnFocus.focus({ preventScroll: true });
  }

  /**
   * Keeps the dialog open until its explicit close button is activated.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!Number.isInteger(index) || index < 0 || index >= this.refs.media.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];
    this.#activeIndex = index;
    for (const [mediaIndex, item] of media.entries()) {
      if (mediaIndex !== index && item.dataset.active === 'true') {
        for (const player of item.querySelectorAll('deferred-media, product-model')) {
          const mediaPlayer = /** @type {HTMLElement & { pauseMedia?: () => void }} */ (player);
          mediaPlayer.pauseMedia?.();
        }
      }
      item.dataset.active = `${mediaIndex === index}`;
    }
    this.#setImageMode('fit');

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    if (targetThumbnail instanceof HTMLElement) {
      scrollIntoView(targetThumbnail, {
        ancestor: thumbnails,
        behavior: options.behavior,
        block: 'center',
        inline: 'center',
      });
    }

    const targetImage = media[index];

    if (targetImage) {
      if (this.#desktopQuery.matches) {
        this.refs.dialog.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      } else {
        targetImage.scrollIntoView({ behavior: options.behavior });
      }

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}
/* [End] */

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
